/**
 * Contact Form Module (Craft CMS Contact Form)
 *
 * Vanilla JS XHR submit with:
 * - Inline error messages per field
 * - Success message below form
 * - Submit button disabled + loading spinner while submitting
 *
 * Usage: <form data-module="contactform" action="" method="post">...</form>
 * Ensure form includes {{ csrfInput() }} and {{ actionInput('contact-form/send') }}
 */

const SPINNER_SVG = `<svg class="contactform-spinner size-6 animate-spin" aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-dasharray="32" stroke-dashoffset="12" stroke-linecap="round"/>
</svg>`;

export class ContactForm {
  #element;
  #submitButton;
  #buttonLabel = "";
  #successEl = null;
  #errorKeys = new Map(); // element -> data-error-key value
  #abortController = null;
  #boundSubmit = this.#onSubmit.bind(this);

  constructor(element) {
    if (!element || element.tagName !== "FORM") return;
    this.#element = element;
    this.#submitButton = element.querySelector('button[type="submit"]');
    if (this.#submitButton) {
      this.#buttonLabel = this.#submitButton.innerHTML.trim();
    }
    this.#cacheErrorContainers();
    this.#cacheSuccessElement();
    this.#element.addEventListener("submit", this.#boundSubmit);
  }

  #cacheErrorContainers() {
    this.#element.querySelectorAll("[data-error-key]").forEach((el) => {
      const key = el.getAttribute("data-error-key");
      if (key) this.#errorKeys.set(key, el);
    });
  }

  #cacheSuccessElement() {
    const wrapper = this.#element.closest("[data-contactform-wrapper]");
    if (wrapper) {
      this.#successEl = wrapper.querySelector("[data-success-message]");
    }
  }

  async #onSubmit(ev) {
    ev.preventDefault();
    if (this.#submitButton?.disabled) return;

    this.#clearMessages();
    this.#setSubmitting(true);

    try {
      // Execute reCAPTCHA v3 to get token
      const token = await this.#getRecaptchaToken();

      const url = this.#element.getAttribute('action') || window.location.pathname || "/";
      const body = new FormData(this.#element);

      // Add reCAPTCHA token to form data
      if (token) {
        body.append('g-recaptcha-response', token);
        body.append('recaptcha-action', 'submit');
      }

      this.#abortController = new AbortController();

      fetch(url, {
        method: "POST",
        body,
        signal: this.#abortController.signal,
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      })
        .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
        .then(({ ok, data }) => {
          if (ok) {
            this.#showSuccess("Bedankt! We nemen zo snel mogelijk contact op.");
            this.#element.reset();
          } else {
            this.#showErrors(data);
          }
        })
        .catch((err) => {
          if (err.name === "AbortError") return;
          this.#showErrors({
            message: "Er is iets misgegaan. Probeer het later opnieuw.",
          });
        })
        .finally(() => {
          this.#setSubmitting(false);
          this.#abortController = null;
        });
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      this.#showErrors({
        message: "Er is iets misgegaan met de beveiligingscontrole. Probeer het later opnieuw.",
      });
      this.#setSubmitting(false);
    }
  }

  /**
   * Get reCAPTCHA v3 token
   * @returns {Promise<string|null>} The reCAPTCHA token or null if not available
   */
  async #getRecaptchaToken() {
    // Check if reCAPTCHA is loaded
    if (typeof grecaptcha === 'undefined' || !grecaptcha.execute) {
      console.warn('reCAPTCHA not loaded');
      return null;
    }

    try {
      // Get site key from data attribute or window
      const siteKey = this.#element.dataset.recaptchaSiteKey || window.RECAPTCHA_SITE_KEY;

      if (!siteKey) {
        console.warn('reCAPTCHA site key not found');
        return null;
      }

      // Execute reCAPTCHA and get token
      const token = await grecaptcha.execute(siteKey, { action: 'submit' });
      return token;
    } catch (error) {
      console.error('Failed to get reCAPTCHA token:', error);
      return null;
    }
  }

  #clearMessages() {
    this.#errorKeys.forEach((el) => {
      el.textContent = "";
      el.classList.add("hidden");
    });
    if (this.#successEl) {
      this.#successEl.textContent = "";
      this.#successEl.classList.add("hidden");
    }
  }

  #showSuccess(text) {
    if (!this.#successEl) return;
    this.#successEl.textContent = text;
    this.#successEl.classList.remove("hidden", "text-red-300");
    this.#successEl.classList.add("text-white");
  }

  #showErrors({ message, errors = {} }) {
    if (message && this.#successEl) {
      this.#successEl.textContent = message;
      this.#successEl.classList.remove("hidden", "text-white");
      this.#successEl.classList.add("text-red-300");
    }
    Object.entries(errors).forEach(([key, list]) => {
      if (!Array.isArray(list)) return;
      // Support both "message.body" and "message[body]" style keys from Craft
      const el = this.#errorKeys.get(key) ?? this.#errorKeys.get(key.replace(/\[(\w+)\]/, ".$1"));
      if (!el) return;
      el.textContent = list.join(" ");
      el.classList.remove("hidden");
    });
  }

  #setSubmitting(submitting) {
    if (!this.#submitButton) return;
    if (submitting) {
      this.#submitButton.disabled = true;
      this.#submitButton.setAttribute("aria-busy", "true");
      this.#submitButton.innerHTML = SPINNER_SVG;
    } else {
      this.#submitButton.disabled = false;
      this.#submitButton.removeAttribute("aria-busy");
      this.#submitButton.innerHTML = this.#buttonLabel;
    }
  }

  transitionOut() {
    this.destroy();
  }

  destroy() {
    if (this.#abortController) this.#abortController.abort();
    this.#element?.removeEventListener("submit", this.#boundSubmit);
    this.#element = null;
    this.#submitButton = null;
    this.#successEl = null;
    this.#errorKeys.clear();
  }
}
